import {
  type AmortListResponse,
  ContractLoanTypeEnum,
  InterestRateStatus,
} from '@kk/api/modules/customerPortal';
import { formatDate } from '@kk/shared/utils/date';
import Table, {
  createTableColumnHelper,
  formatCellCurrency,
  getRowYearClassName,
  type kkTableFeatures,
} from '@kk/ui/components/Table';
import type { CellContext, Row } from '@tanstack/react-table';
import { cn } from 'cn';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { match, P } from 'ts-pattern';

const columnHelper = createTableColumnHelper<AmortListResponse>();

type InterestRateStatusLabel =
  | 'standalone.determineAtTerm'
  | 'standalone.newInterestAgreement';

function getInterestRateStatusLabel(
  loanProduct: ContractLoanTypeEnum | undefined,
  interestRateStatus: InterestRateStatus | undefined,
): InterestRateStatusLabel | undefined {
  return match([loanProduct, interestRateStatus])
    .returnType<InterestRateStatusLabel | undefined>()
    .with(
      [
        ContractLoanTypeEnum.KKvar,
        P.union(
          InterestRateStatus.BeforeInterestConditionExpiryDate,
          InterestRateStatus.AfterInterestConditionExpiryDate,
        ),
      ],
      () => 'standalone.determineAtTerm',
    )
    .with(
      [
        ContractLoanTypeEnum.KKfast,
        P.union(
          InterestRateStatus.BeforeInterestConditionExpiryDate,
          InterestRateStatus.AfterInterestConditionExpiryDate,
        ),
      ],
      () => 'standalone.newInterestAgreement',
    )
    .with(
      [
        ContractLoanTypeEnum.KKcibor,
        InterestRateStatus.BeforeInterestConditionExpiryDate,
      ],
      () => 'standalone.determineAtTerm',
    )
    .with(
      [
        ContractLoanTypeEnum.KKcibor,
        InterestRateStatus.AfterInterestConditionExpiryDate,
      ],
      () => 'standalone.newInterestAgreement',
    )
    .otherwise(() => undefined);
}

export interface AmortListTableProps {
  data?: AmortListResponse[] | null;
  className?: string;
  loanProduct: ContractLoanTypeEnum | undefined;
  hideFirstRow?: boolean;
}

export function AmortListTable({
  data,
  className,
  loanProduct,
  hideFirstRow = true,
}: AmortListTableProps) {
  const { t } = useTranslation(['ui']);

  const formatCellToInterestValue = useCallback(
    (info: CellContext<typeof kkTableFeatures, AmortListResponse, number>) => {
      const { interestRateStatus } = info.row.original;
      const interestRateStatusLabel = getInterestRateStatusLabel(
        loanProduct,
        interestRateStatus,
      );

      return interestRateStatusLabel
        ? t(interestRateStatusLabel)
        : formatCellCurrency(info, { hideFirstRow });
    },
    [t, loanProduct, hideFirstRow],
  );

  const columns = useMemo(
    () =>
      columnHelper.columns([
        columnHelper.accessor('valueDate', {
          header: () => t('data.valueDate'),
          size: 150,
          cell: (info) => {
            return formatDate(info.getValue());
          },
        }),
        columnHelper.accessor('repayment', {
          header: () => t('data.repayment'),
          cell: (info) =>
            formatCellCurrency(info, { hideFirstRow: hideFirstRow }),
          meta: {
            rightAlign: true,
          },
        }),
        columnHelper.accessor('interest', {
          header: () => t('data.interest'),
          cell: formatCellToInterestValue,
          meta: {
            rightAlign: true,
          },
        }),
        columnHelper.accessor('margin', {
          header: () => t('data.margin'),
          cell: (info) =>
            formatCellCurrency(info, { hideFirstRow: hideFirstRow }),
          meta: {
            rightAlign: true,
          },
        }),
        columnHelper.accessor('payment', {
          header: () => t('data.payment'),
          cell: formatCellToInterestValue,
          meta: {
            rightAlign: true,
          },
        }),
        columnHelper.accessor('notionalAmount', {
          header: () => t('data.notionalAmount'),
          cell: formatCellCurrency,
          meta: {
            rightAlign: true,
          },
        }),
      ]),
    [t, formatCellToInterestValue, hideFirstRow],
  );

  const getRowClassName = useCallback(
    (row: Row<typeof kkTableFeatures, AmortListResponse>) => {
      if (!data) return '';
      const firstYearInData = data[0]?.valueDate
        ? new Date(data[0].valueDate).getFullYear()
        : 0;
      const value: string = row.getValue('valueDate');
      const bg = getRowYearClassName(value, firstYearInData);

      return bg;
    },
    [data],
  );

  return (
    <div className={cn('grid', className)}>
      <Table<AmortListResponse>
        stickyHeader
        data={data || []}
        className={cn(
          'border-neutral-10 content-visibility-auto screen:max-h-248 overflow-y-auto rounded-md border',
          'print:max-h-none print:overflow-visible print:text-xs',
        )}
        variant="relaxed"
        columns={columns}
        getRowClassName={getRowClassName}
      />
    </div>
  );
}
